import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_FEATURE_CODES, type Extension } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useFeatureCodes } from '../../lib/api/hooks-tms.js';
import { Badge, CardHead } from '../../components/ui/Primitives.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * What this person types at a shared desk phone: their extension and a PIN (owner, 16
 * September 2026).
 *
 * On Phone setup, beside the desk phones, because setting up a hot desk phone and giving
 * somebody the means to sit down at it is one job. The extension's own Hot desking section
 * keeps the history and the sessions; this is the two fields that make a login possible.
 *
 * The PIN is stored as a hash and never read back, so the field is write-only: the badge
 * says whether one is set, and the button replaces it.
 */

interface HotDeskState {
  included: boolean;
  has_pin: boolean;
  session: { started_at: string; device_label: string | null; sip_username: string | null } | null;
}

export function HotDeskLogin({ tenantId, ext, mayWrite }: {
  tenantId: string; ext: Extension; mayWrite: boolean;
}) {
  const qc = useQueryClient();
  const key = ['tenants', tenantId, 'extensions', ext.id, 'hot-desk'] as const;
  const state = useQuery({
    queryKey: key,
    queryFn: () => api.get<HotDeskState>(`/v1/tenants/${tenantId}/extensions/${ext.id}/hot-desk`),
  });
  const codes = useFeatureCodes(tenantId).data?.codes ?? DEFAULT_FEATURE_CODES;
  const [pin, setPin] = useState('');

  const savePin = useMutation({
    mutationFn: (value: string | null) =>
      api.put(`/v1/tenants/${tenantId}/hot-desk/pin/${ext.id}`, { pin: value }),
    onSuccess: () => { setPin(''); return qc.invalidateQueries({ queryKey: key }); },
  });

  const s = state.data;

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title="Hot desk login"
        action={s?.session && <Badge tone="ok">sitting at {s.session.device_label ?? s.session.sip_username ?? 'a desk'}</Badge>} />
      <div className="arc-card-body">
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
          At a shared desk phone, press <strong>Log in</strong> and enter{' '}
          <strong className="arc-mono">{ext.number}</strong> and this PIN.
          {codes.hotdesk_login && (
            <>
              {' '}On a phone without those keys, dial{' '}
              <span className="arc-mono">{codes.hotdesk_login}{ext.number}*</span> then the PIN
              {codes.hotdesk_logout
                ? <>; <span className="arc-mono">{codes.hotdesk_logout}</span> logs out again.</>
                : '.'}
            </>
          )}
        </p>

        <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge tone={s?.has_pin ? 'ok' : 'muted'}>{s?.has_pin ? 'PIN set' : 'no PIN'}</Badge>
          <input className="arc-input arc-mono" type="password" inputMode="numeric"
            autoComplete="new-password" style={{ maxWidth: 160 }} disabled={!mayWrite}
            aria-label={`PIN for ${ext.number}`}
            placeholder={s?.has_pin ? 'New PIN' : 'Choose a PIN'} value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            disabled={!mayWrite || pin.length < 4 || savePin.isPending}
            onClick={() => savePin.mutate(pin)}>
            {s?.has_pin ? 'Change PIN' : 'Set PIN'}
          </button>
          {s?.has_pin && mayWrite && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              disabled={savePin.isPending}
              title="Stops this extension logging in anywhere, and ends any login now"
              onClick={() => {
                void confirmAction({
                  title: 'Clear the PIN?',
                  body: `${ext.number} is logged out of any shared desk and cannot log in again until a new PIN is set.`,
                  action: 'Clear PIN', danger: true,
                }).then((ok) => { if (ok) savePin.mutate(null); });
              }}>
              Clear PIN
            </button>
          )}
          <span className="arc-subtle" style={{ fontSize: 11.5 }}>4 to 8 digits</span>
        </div>

        {/* A PIN is not enough on its own: the seat's type has to include hot desking. */}
        {s && !s.included && (
          <p className="arc-hint" style={{ marginBottom: 0 }}>
            Hot desking is not included in this extension’s type, so the login is refused. Add it
            on the Features section, or move the extension to a type that includes it.
          </p>
        )}
      </div>
    </div>
  );
}
